import { SECOND_IN_MILLISECONDS } from "Constants";
import {
  CallStatus,
  useDeviceStateSubscriptionQuery,
  DeviceFragment,
  useHardwareModelDetailsQuery,
  useTenantRelayDevicesQuery,
  Omit,
  TenantRelayDevicesQueryVariables,
  DeviceSortDirection
} from "generated/graphql";
import * as Urql from "polygraphql";
import { useEffect } from "react";

/**
 * Fetches if you are currently in a call from just the cache
 * so that is cannot overrun any other live data hooks.
 * @param id
 */
export const useDeviceCallState = (
  id?: string
): {
  status: CallStatus;
  inCall: boolean;
} => {
  const { data, executor } = useDeviceStateSubscriptionQuery({
    variables: {
      id: id ?? ""
    },
    pause: !id
  });

  useEffect(() => {
    if (id) {
      const intervalId = setInterval(() => {
        executor({ requestPolicy: "cache-only" });
      }, 5 * SECOND_IN_MILLISECONDS);
      return () => clearInterval(intervalId);
    }
  }, [id, executor]);

  const status = data?.device?.callStatus ?? CallStatus.Unknown;
  return {
    status,
    inCall: status === "IN_CALL"
  };
};

/**
 * Determines if a device is of specified model.
 * @param device
 */
export const useIsLensApp = (
  device: DeviceFragment | undefined,
  modelId: string
) => {
  const { data } = useHardwareModelDetailsQuery({
    variables: {
      id: modelId
    }
  });
  if (!device?.hardwareModel) {
    return false;
  }

  return (
    data?.hardwareModel.name === device.hardwareModel ||
    data?.hardwareModel.aliases?.includes(device.hardwareModel)
  );
};

/**
 * Determines if a device is connected to a lens desktop app.
 * @param device
 */
export const useIsConnectedToApp = (
  device: DeviceFragment | undefined,
  appModelId: string
) => {
  const { data: desktopModelName } = useHardwareModelDetailsQuery({
    variables: {
      id: appModelId
    }
  });
  if (!device?.entityConnections) {
    return false;
  }

  const appModelNames = [desktopModelName?.hardwareModel.name].concat(
    desktopModelName?.hardwareModel.aliases
  );
  return (
    device?.entityConnections?.edges?.some(connection => {
      if (!connection.node.targetEntity.hardwareModel) {
        return false;
      }
      return (
        appModelNames.includes(connection.node.targetEntity.hardwareModel) &&
        connection.node.targetEntity.connected &&
        connection.node.connected
      );
    }) || false
  );
};

/**
 * Determines if a device is connected to a lens app
 * and if desktop, lens apps software version is >= 1.1.3
 * if mobile, lens apps software version is >= 2099
 * @param device
 */
export const useIsCompatibleVersion = (
  device: DeviceFragment | undefined,
  appModelId: string
) => {
  const { data } = useHardwareModelDetailsQuery({
    variables: {
      id: appModelId
    }
  });
  if (!device?.entityConnections) {
    return false;
  }

  return (
    device?.entityConnections?.edges?.some(connection => {
      if (!connection.node.targetEntity.hardwareModel) {
        return false;
      }
      const isHardwareModelMatch =
        data?.hardwareModel.name ===
          connection.node.targetEntity.hardwareModel ||
        data?.hardwareModel.aliases?.includes(
          connection.node.targetEntity.hardwareModel
        );
      const isDesktopAppVersionCompatible =
        appModelId === "Lens Desktop" &&
        isHardwareModelMatch &&
        isCompatible(connection.node.targetEntity.softwareVersion, "1.1.3");
      const isMobileAppVersionCompatible =
        appModelId === "Lens Mobile" &&
        isHardwareModelMatch &&
        connection.node.targetEntity.softwareVersion &&
        parseInt(connection.node.targetEntity.softwareVersion) >= 2099;
      return isDesktopAppVersionCompatible || isMobileAppVersionCompatible;
    }) || false
  );
};

export const isCompatible = (
  softwareVersion: string | null,
  targetVersion: string
) => {
  if (!softwareVersion) {
    return false;
  }
  const [major, minor, patch] = softwareVersion.split(".").map((v: string) => {
    if (v.includes("-")) {
      v = v.split("-")[0];
    }
    return parseInt(v);
  });
  const [majorTarget, minorTarget, patchTarget] = targetVersion
    .split(".")
    .map((v: string) => parseInt(v));

  if (major !== majorTarget) {
    return major > majorTarget;
  }
  if (minor !== minorTarget) {
    return minor > minorTarget;
  }
  return patch >= patchTarget;
};

export const useTenantRelayDevices = (tenantId: string, limit = 100) => {
  const options: Omit<
    Urql.UseQueryArgs<TenantRelayDevicesQueryVariables>,
    "query"
  > = {
    variables: {
      filter: {
        AND: [
          { field: "hardwareModel", contains: "Lens" },
          { field: "hardwareModel", contains: "Relay" },
          { field: "tenantId", eq: tenantId }
        ]
      },
      limit,
      sort: {
        fields: [{ name: "name", direction: DeviceSortDirection.Asc }]
      },
      from: 0
    }
  };

  const { data, fetching } = useTenantRelayDevicesQuery(options);

  return fetching ? [] : data?.devices || [];
};
